import { zodResolver } from '@hookform/resolvers/zod';
import { Check, UserRound } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button, FormField, InlineAlert, Input, StatusPill } from '@/shared/ui';

import { identifyCustomerSchema } from '../schemas/identifyCustomerSchema';
import type { OfflineCustomerSnapshot, OfflineSaleSession } from '../types';

const offlineCustomerFormSchema = identifyCustomerSchema.extend({
  customerName: z.string().trim().max(120, 'checkout.nameTooLong'),
});

type OfflineCustomerFormValues = z.infer<typeof offlineCustomerFormSchema>;

export interface OfflineCustomerFormProps {
  session: OfflineSaleSession;
  snapshot: OfflineCustomerSnapshot | null;
  phone: string;
  name: string;
  identifying?: boolean;
  clearing?: boolean;
  error?: string | null;
  onPhoneChange: (phone: string) => void;
  onNameChange: (name: string) => void;
  onIdentify: (phone: string) => Promise<void>;
  onClear: () => Promise<void>;
}

export function OfflineCustomerForm({
  session,
  snapshot,
  phone,
  name,
  identifying = false,
  clearing = false,
  error,
  onPhoneChange,
  onNameChange,
  onIdentify,
  onClear,
}: OfflineCustomerFormProps) {
  const { t } = useTranslation('offlineOrders');
  const form = useForm<OfflineCustomerFormValues>({
    resolver: zodResolver(offlineCustomerFormSchema),
    values: { phoneNumber: phone, customerName: name },
  });

  const status = snapshot?.status ?? '';
  const statusKey =
    status === 'ACTIVE' || status === 'SUSPENDED' ? (`customer.status.${status}` as const) : null;

  return (
    <section className="flex flex-col gap-3 border-t border-border pt-4">
      <h2 className="text-sm font-semibold text-fg">{t('customer.title')}</h2>
      <p className="text-xs text-fg-muted">{t('customer.optional')}</p>

      <form
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit(async (values) => {
          await onIdentify(values.phoneNumber);
        })}
      >
        <FormField
          error={
            form.formState.errors.phoneNumber?.message
              ? t(form.formState.errors.phoneNumber.message as 'customer.phone.invalid')
              : undefined
          }
        >
          <FormField.Label>{t('customer.phoneLabel')}</FormField.Label>
          <FormField.Control>
            <Input
              autoComplete="off"
              inputMode="tel"
              placeholder={t('customer.phonePlaceholder')}
              disabled={identifying}
              {...form.register('phoneNumber', {
                onChange: (event) => onPhoneChange(event.target.value),
              })}
            />
          </FormField.Control>
        </FormField>

        <FormField
          error={
            form.formState.errors.customerName?.message ? t('checkout.nameTooLong') : undefined
          }
        >
          <FormField.Label>{t('customer.name')}</FormField.Label>
          <FormField.Control>
            <Input
              autoComplete="name"
              placeholder={t('customer.namePlaceholder')}
              {...form.register('customerName', {
                onChange: (event) => onNameChange(event.target.value),
              })}
            />
          </FormField.Control>
        </FormField>

        {error ? <InlineAlert>{error}</InlineAlert> : null}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="secondary" disabled={identifying}>
            {identifying ? t('customer.identifying') : t('customer.identify')}
          </Button>
          {session.customerPhoneNumber ? (
            <Button
              type="button"
              variant="ghost"
              disabled={clearing}
              onClick={() => void onClear()}
            >
              {t('customer.clear')}
            </Button>
          ) : null}
        </div>
      </form>

      {session.customerPhoneNumber ? (
        <div className="rounded-md border border-border bg-surface-muted p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill
              tone={session.customerRegistered ? 'success' : 'neutral'}
              icon={session.customerRegistered ? Check : UserRound}
            >
              {session.customerRegistered ? t('customer.registered') : t('customer.walkIn')}
            </StatusPill>
            {statusKey ? (
              <StatusPill tone={status === 'ACTIVE' ? 'success' : 'danger'} icon={UserRound}>
                {t(statusKey)}
              </StatusPill>
            ) : null}
          </div>
          <p className="mt-2 font-medium text-fg">
            {session.customerName || snapshot?.fullName || session.customerPhoneNumber}
          </p>
          <p className="text-fg-muted">{session.customerPhoneNumber}</p>
          {snapshot?.email ? (
            <p className="text-fg-muted">
              {t('customer.email')}: {snapshot.email}
            </p>
          ) : null}
          <p className="mt-2 text-fg-muted">
            {session.customerRegistered
              ? t('customer.registeredHelp')
              : t('customer.unregisteredHelp')}
          </p>
        </div>
      ) : null}
    </section>
  );
}
